<template>
  <div class="public-results-page">
    <div class="public-results-container">
      <div class="public-results-brand">
        <span class="brand-icon">
          <i class="fa fa-play"></i>
        </span>
        <span class="brand-text">Clipr</span>
      </div>

      <!-- Header -->
      <div class="public-results-header">
        <div class="header-content">
          <h2 class="public-results-title">{{ tt("publicResults.title", "Clips renderizados") }}</h2>
          <p class="public-results-count" v-if="!loading && !error">
            {{ clips.length }} clip{{ clips.length === 1 ? "" : "s" }} renderizado{{ clips.length === 1 ? "" : "s" }}
          </p>
          <p class="public-results-subtitle" v-if="jobIdShort">
            {{ tt("publicResults.job", "Tarea") }}: <strong>{{ jobIdShort }}</strong>
          </p>
          <p v-else class="public-results-subtitle">{{ tt("common.loading", "Cargando...") }}</p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="public-results-state text-center">
        <clipr-loader
          :title="tt('common.loading', 'Cargando...')"
        />
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="public-results-state text-center">
        <p class="text-danger mb-2">{{ error }}</p>
        <button class="btn btn-outline-primary btn-sm" @click="fetchClips">
          {{ tt("results.retry", "Reintentar") }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="clips.length === 0" class="public-results-state text-center">
        <i class="ti-video-camera mb-3 empty-state-icon"></i>
        <p class="text-muted mb-2">{{ tt("publicResults.empty", "No se encontraron clips renderizados para esta tarea.") }}</p>
      </div>

      <!-- Clips List -->
      <div v-else class="public-results-clips-list">
        <div v-for="(clip, index) in clips" :key="clip.id" class="public-result-clip-item">
          <!-- Columna izquierda: Video player -->
          <div class="public-result-clip-media">
            <div class="public-result-clip-thumbnail-wrapper">
              <video
                v-if="clip.rendered_video_url"
                :class="[
                  'public-result-clip-video',
                  { 'public-result-clip-video--hidden-preview': !clip.thumbnailUrl && !clip.hasStartedPlayback }
                ]"
                :src="clip.rendered_video_url"
                :poster="clip.thumbnailUrl || null"
                loop
                playsinline
                preload="metadata"
                controls
                @loadedmetadata="onVideoLoaded($event, clip)"
                @play="onVideoPlay($event, clip)"
                @timeupdate="onVideoTimeUpdate($event, clip)"
                @ended="onVideoEnded($event, clip)"
                @pause="onVideoPause($event, clip)"
              ></video>
              <button
                v-if="clip.rendered_video_url && !clip.isPlaying"
                type="button"
                class="public-center-play-btn"
                @click.stop="toggleClipPlaybackFromOverlay(clip)"
              >
                <i class="ti-control-play"></i>
              </button>
              <div v-if="!clip.rendered_video_url" class="public-result-clip-thumbnail-placeholder">
                <i class="ti-control-play public-play-icon"></i>
              </div>
              <div class="public-result-clip-overlay"></div>
            </div>
          </div>

          <!-- Columna central: Información del clip -->
          <div class="public-result-clip-content">
            <!-- Score badge en esquina superior derecha -->
            <div class="public-result-clip-score" v-if="clip.confidence != null" :class="getScoreClass(clip.confidence)">
              <div class="score-value">{{ (clip.confidence * 100).toFixed(0) }}</div>
              <div class="score-label">{{ tt("publicResults.score", "PUNTAJE") }}</div>
            </div>

            <div class="public-result-clip-header">
              <h3 class="public-result-clip-title">
                <span class="clip-number">#{{ index + 1 }}</span>
                <span class="clip-title-text">{{ clip.title || "Clip sin título" }}</span>
              </h3>
              <div class="public-result-clip-meta">
                <span class="status-pill" :class="'status-' + (clipStatusClassToken(clip.status) || 'accepted')">
                  <i class="ti-check status-icon" v-if="['2_accepted', '4_rendered', '5_scheduled_to_post', '7_posted'].includes(String(clip.status || '').toLowerCase())"></i>
                  <i class="ti-clock status-icon" v-else-if="['1_pending', '3_rendering'].includes(String(clip.status || '').toLowerCase())"></i>
                  <i class="ti-alert-circle status-icon" v-else-if="['2_rejected', '2.1_failed_insufficient_tokens', '3.1_failed', '6.1_post_failed'].includes(String(clip.status || '').toLowerCase())"></i>
                  {{ statusLabel(clip.status || "5_scheduled_to_post") }}
                </span>
                <span v-if="clip.created_at" class="public-timestamp">
                  <i class="ti-calendar"></i> {{ formatDate(clip.created_at) }}
                </span>
              </div>
            </div>

            <!-- Transcript -->
            <div v-if="clip.transcript_excerpt" class="public-result-clip-transcript">
              <div class="section-header">
                <i class="ti-align-left transcript-icon"></i>
                <span class="section-title">Transcripción</span>
              </div>
              <div class="section-content">{{ clip.transcript_excerpt }}</div>
            </div>

            <!-- Acciones: Descargar y Eliminar -->
            <div class="public-result-clip-actions">
              <button
                class="public-download-btn"
                type="button"
                :disabled="isClipDownloading(clip) || isClipDeleting(clip)"
                @click="downloadClip(clip)"
              >
                <i class="ti-download"></i>
                <span>{{ isClipDownloading(clip) ? tt("publicResults.downloading", "Descargando...") : tt("publicResults.download", "Descargar") }}</span>
              </button>
              <button
                class="public-delete-btn"
                type="button"
                :disabled="isClipDeleting(clip) || isClipDownloading(clip)"
                @click="requestDeleteClip(clip)"
              >
                <i class="ti-trash"></i>
                <span>{{ isClipDeleting(clip) ? tt("publicResults.deleting", "Eliminando...") : tt("common.delete", "Eliminar") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <transition name="modal-fade">
      <body-portal v-if="showDeleteModal && pendingDeleteClip">
        <div
          class="confirm-modal-overlay"
          @click.self="closeDeleteModal"
        >
        <div class="confirm-modal public-delete-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div class="confirm-modal-header">
            <div>
              <h3 id="delete-confirm-title" class="confirm-modal-title">
                {{ deleteConfirmStep === 1 ? tt("publicResults.confirmDelete", "Confirmar eliminación") : tt("publicResults.lastConfirmation", "Última confirmación") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ deleteConfirmStep === 1
                  ? tt("publicResults.deleteStepOne", "Este clip dejará de mostrarse en resultados públicos.")
                  : tt("publicResults.deleteStepTwo", "Esta acción es permanente y no se podrá deshacer.") }}
              </p>
            </div>
            <button class="confirm-modal-close" type="button" @click="closeDeleteModal" :disabled="isDeletingPendingClip">
              <i class="ti-close"></i>
            </button>
          </div>

          <div class="confirm-modal-body">
            <div class="delete-confirm-summary">
              <span class="delete-confirm-label">{{ tt("publicResults.selectedClip", "Clip seleccionado") }}</span>
              <strong class="delete-confirm-title">{{ pendingDeleteClip.title || "Clip sin título" }}</strong>
            </div>

            <p class="delete-confirm-warning" v-if="deleteConfirmStep === 2">
              {{ tt("publicResults.deleteWarning", "Si continúas, el clip será eliminado y ya no volverá a estar disponible.") }}
            </p>

            <p v-if="deleteModalError" class="delete-confirm-error">
              <i class="ti-alert-circle"></i> {{ deleteModalError }}
            </p>
          </div>

          <div class="confirm-modal-footer">
            <button class="confirm-modal-btn confirm-modal-btn--secondary" type="button" @click="closeDeleteModal" :disabled="isDeletingPendingClip">
              {{ tt("usersList.cancel", "Cancelar") }}
            </button>
            <button
              class="confirm-modal-btn"
              :class="deleteConfirmStep === 1 ? 'confirm-modal-btn--primary' : 'confirm-modal-btn--danger'"
              type="button"
              :disabled="isDeletingPendingClip"
              @click="confirmDeleteStep"
            >
              {{ isDeletingPendingClip
                ? tt("publicResults.deleting", "Eliminando...")
                : (deleteConfirmStep === 1 ? tt("publicResults.yesContinue", "Sí, continuar") : tt("publicResults.yesDeletePermanent", "Sí, eliminar definitivamente")) }}
            </button>
          </div>
        </div>
        </div>
      </body-portal>
    </transition>

    <!-- Snackbar de confirmación -->
    <transition name="snackbar-fade">
      <div
        v-if="snackbarVisible"
        class="public-snackbar"
        :class="snackbarType === 'delete' ? 'public-snackbar--delete' : 'public-snackbar--download'"
      >
        <i :class="snackbarType === 'delete' ? 'ti-trash' : 'ti-download'"></i>
        <span>{{ snackbarMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import escapeToCloseMixin from "@/mixins/escapeToCloseMixin";
import CliprLoader from "@/components/clipr/CliprLoader.vue";
import {
  deleteTwitchClip,
  fetchTwitchAdminResults,
  requestTwitchClipDownload,
} from "@/api/twitch";
import { invalidateAllScheduleClipLibraryCaches } from "@/utils/scheduleClipLibraryCache";

export default {
  name: "PublicResults",
  components: {
    CliprLoader,
  },
  mixins: [escapeToCloseMixin],
  data() {
    return {
      loading: true,
      error: null,
      clips: [],
      jobId: null,
      videoElements: {}, // Store references to video elements
      downloadingClipIds: {},
      deletingClipIds: {},
      showDeleteModal: false,
      pendingDeleteClip: null,
      deleteConfirmStep: 1,
      deleteModalError: "",
      snackbarVisible: false,
      snackbarMessage: "",
      snackbarType: "download",
      snackbarTimeout: null,
    };
  },
  computed: {
    escapeToCloseModals() {
      return [{ show: "showDeleteModal", close: "closeDeleteModal" }];
    },
    jobIdShort() {
      if (!this.jobId) return "";
      return typeof this.jobId === "string" ? this.jobId.slice(0, 8) : "N/D";
    },
    isDeletingPendingClip() {
      return this.isClipDeleting(this.pendingDeleteClip);
    },
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    getClipVideoKey(clip) {
      if (!clip) return "";
      return clip.id || `clip-${clip.rendered_video_url || ""}`;
    },
    toggleClipPlaybackFromOverlay(clip) {
      const clipKey = this.getClipVideoKey(clip);
      const video = clipKey ? this.videoElements[clipKey] : null;
      if (!video || typeof video.play !== "function") return;
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    },
    resolveClipThumbnailUrl(clip) {
      if (!clip || typeof clip !== "object") return null;
      return typeof clip.thumbnail_url === "string" && clip.thumbnail_url
        ? clip.thumbnail_url
        : null;
    },
    statusLabel(status) {
      if (!status) return this.tt("publicResults.status.readyToPost", "Listo para publicar");
      const normalized = String(status).toLowerCase();
      const map = {
        "1_pending": this.tt("publicResults.status.pending", "Pendiente"),
        "2_accepted": this.tt("publicResults.status.accepted", "Aceptado"),
        "2_rejected": this.tt("publicResults.status.rejected", "Rechazado"),
        "2.1_failed_insufficient_tokens": this.tt("publicResults.status.failed", "Fallido"),
        "3.1_failed": this.tt("publicResults.status.failed", "Fallido"),
        "3_rendering": this.tt("publicResults.status.rendering", "Renderizando"),
        "4_rendered": this.tt("publicResults.status.rendered", "Listo"),
        "5_scheduled_to_post": this.tt("publicResults.status.readyToPost", "Listo para publicar"),
        "6.1_post_failed": this.tt("publicResults.status.postFailed", "Publicación fallida"),
        "7_posted": this.tt("publicResults.status.posted", "Publicado"),
      };
      return map[normalized] || status;
    },
    clipStatusClassToken(status) {
      const normalized = String(status || "").toLowerCase();
      if (["2_accepted", "4_rendered", "5_scheduled_to_post", "7_posted"].includes(normalized)) return "accepted";
      if (["1_pending"].includes(normalized)) return "pending";
      if (["3_rendering"].includes(normalized)) return "rendering";
      if (["2_rejected", "2.1_failed_insufficient_tokens", "3.1_failed", "6.1_post_failed"].includes(normalized)) return "failed";
      return "pending";
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      // Format with 24-hour time format
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      };
      return d.toLocaleString('es', options);
    },
    clipIdDisplay(id) {
      if (!id) return "N/D";
      const s = String(id);
      const first = s.split("-")[0];
      return s.includes("-") ? `${first}...` : s;
    },
    getScoreClass(confidence) {
      if (!confidence) return "score-low";
      const score = confidence * 100;
      if (score >= 80) return "score-excellent";
      if (score >= 60) return "score-good";
      return "score-low";
    },
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return "0:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    showSnackbar(message, type = "download") {
      if (this.snackbarTimeout) clearTimeout(this.snackbarTimeout);
      this.snackbarMessage = message;
      this.snackbarType = type;
      this.snackbarVisible = true;
      this.snackbarTimeout = setTimeout(() => {
        this.snackbarVisible = false;
        this.snackbarTimeout = null;
      }, 3500);
    },
    isClipDownloading(clip) {
      const clipKey = clip && clip.id ? String(clip.id) : "";
      return Boolean(clipKey && this.downloadingClipIds[clipKey]);
    },
    isClipDeleting(clip) {
      const clipKey = clip && clip.id ? String(clip.id) : "";
      return Boolean(clipKey && this.deletingClipIds[clipKey]);
    },
    async downloadClip(clip) {
      const clipId = clip && clip.id ? String(clip.id) : "";
      if (!clipId || !this.jobId || this.isClipDownloading(clip) || this.isClipDeleting(clip)) return;

      this.$set(this.downloadingClipIds, clipId, true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        const payload = await requestTwitchClipDownload(
          session,
          clipId,
          window.crypto.randomUUID()
        );
        if (!payload.url) {
          throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
        }
        const response = await fetch(payload.url);
        if (!response.ok) {
          throw new Error(this.tt("publicResults.downloadError", "No se pudo descargar el clip."));
        }
        const blobUrl = window.URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `clip-${clipId.slice(0, 8)}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.showSnackbar(this.tt("publicResults.downloadSuccess", "Clip descargado correctamente"), "download");
      } catch (err) {
        console.error("Error downloading clip:", err);
        window.alert(err.message || this.tt("publicResults.downloadRetryError", "Error al descargar el clip. Inténtalo de nuevo."));
      } finally {
        this.$delete(this.downloadingClipIds, clipId);
      }
    },
    requestDeleteClip(clip) {
      const clipId = clip && clip.id ? String(clip.id) : "";
      if (!clipId || !this.jobId || this.isClipDeleting(clip) || this.isClipDownloading(clip)) return;

      this.pendingDeleteClip = clip;
      this.deleteConfirmStep = 1;
      this.deleteModalError = "";
      this.showDeleteModal = true;
    },
    closeDeleteModal(force = false) {
      if (this.isDeletingPendingClip && !force) return;
      this.showDeleteModal = false;
      this.pendingDeleteClip = null;
      this.deleteConfirmStep = 1;
      this.deleteModalError = "";
    },
    async confirmDeleteStep() {
      if (!this.pendingDeleteClip || this.isDeletingPendingClip) return;

      if (this.deleteConfirmStep === 1) {
        this.deleteConfirmStep = 2;
        this.deleteModalError = "";
        return;
      }

      const clipId = String(this.pendingDeleteClip.id || "");
      if (!clipId) return;

      this.$set(this.deletingClipIds, clipId, true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        await deleteTwitchClip(session, clipId);

        this.clips = (this.clips || []).filter((item) => String(item && item.id) !== clipId);
        this.$delete(this.videoElements, clipId);
        invalidateAllScheduleClipLibraryCaches();
        this.closeDeleteModal(true);
        this.showSnackbar(this.tt("publicResults.deleteSuccess", "Clip eliminado"), "delete");
      } catch (err) {
        console.error("Error deleting clip:", err);
        this.deleteModalError = err.message || this.tt("publicResults.deleteRetryError", "Error al eliminar el clip. Inténtalo de nuevo.");
      } finally {
        this.$delete(this.deletingClipIds, clipId);
      }
    },
    onVideoLoaded(event, clip) {
      const video = event.target;
      if (video) {
        // Store reference to the video element
        const clipKey = this.getClipVideoKey(clip);
        if (clipKey) {
          this.videoElements[clipKey] = video;
        }
        this.$set(clip, "isPlaying", false);
        this.$set(clip, "hasStartedPlayback", false);
        
        if (video.duration) {
          this.$set(clip, "videoTotalDuration", video.duration);
          this.$set(clip, "videoDuration", this.formatDuration(video.duration));
          this.$set(clip, "videoCurrentTime", 0);
        }
      }
    },
    onVideoPlay(event, clip) {
      const video = event && event.target;
      if (!video) return;
      
      // Pausar todos los demás videos cuando se reproduce este
      Object.keys(this.videoElements).forEach((key) => {
        const otherVideo = this.videoElements[key];
        if (otherVideo && otherVideo !== video && !otherVideo.paused) {
          otherVideo.pause();
        }
      });
      (this.clips || []).forEach((item) => {
        this.$set(item, "isPlaying", item === clip);
      });
      this.$set(clip, "hasStartedPlayback", true);
    },
    onVideoTimeUpdate(event, clip) {
      const video = event.target;
      if (video && !video.paused && video.currentTime !== undefined) {
        this.$set(clip, "videoCurrentTime", video.currentTime);
        this.$set(clip, "videoDuration", this.formatDuration(video.currentTime));
      }
    },
    onVideoEnded(event, clip) {
      const video = event.target;
      if (video && clip.videoTotalDuration) {
        this.$set(clip, "videoCurrentTime", 0);
        this.$set(clip, "videoDuration", this.formatDuration(clip.videoTotalDuration));
      }
      this.$set(clip, "isPlaying", false);
    },
    onVideoPause(event, clip) {
      const video = event.target;
      if (video && clip.videoTotalDuration) {
        // When paused, always show total duration
        this.$set(clip, "videoDuration", this.formatDuration(clip.videoTotalDuration));
      }
      this.$set(clip, "isPlaying", false);
    },
    async fetchClips() {
      if (!this.jobId) {
        this.error = this.tt("publicResults.invalidJobId", "ID de tarea inválido.");
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error(this.tt("common.noActiveSession", "No hay sesión activa."));
        const payload = await fetchTwitchAdminResults(session, this.jobId);
        this.clips = (payload.clips || [])
          .filter((row) =>
            row?.rendered_video_url
            && [
              "4_rendered",
              "4.3_rendered",
              "5_scheduled_to_post",
              "6_posted",
              "7_posted",
              "posted",
            ].includes(String(row.status || "").toLowerCase())
          )
          .map((row) => ({
            ...row,
            thumbnailUrl: this.resolveClipThumbnailUrl(row),
          }));
      } catch (err) {
        console.error("Error loading rendered clips:", err);
        this.error = err.message || this.tt("results.genericError", "Error al cargar los resultados. Por favor, inténtalo de nuevo.");
        this.clips = [];
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    // Get jobId from route
    this.jobId = this.$route.params.jobId;
    if (this.jobId) {
      await this.fetchClips();
    } else {
      this.error = this.tt("publicResults.jobIdNotFound", "ID de tarea no encontrado en la URL.");
      this.loading = false;
    }
  },
  watch: {
    "$route.params.jobId"(newJobId) {
      this.jobId = newJobId;
      if (this.jobId) {
        this.fetchClips();
      }
    },
  },
  beforeDestroy() {
    if (this.snackbarTimeout) clearTimeout(this.snackbarTimeout);
  },
};
</script>

<style scoped>
.public-results-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(168, 85, 247, 0.18) 0%, transparent 55%),
    radial-gradient(circle at bottom, rgba(59, 130, 246, 0.18) 0%, transparent 55%),
    var(--clipr-bg-primary);
  padding: 40px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
}

.public-results-container {
  max-width: 1200px;
  width: 100%;
  border-radius: var(--clipr-radius-2xl);
  background: var(--clipr-bg-card);
  border: 1px solid var(--clipr-border-color);
  box-shadow: 0 40px 90px rgba(0, 0, 0, 0.85), 0 0 70px var(--clipr-purple-glow);
  padding: 40px;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(26px);
}

.public-results-header {
  margin-bottom: 40px;
  padding: 22px 20px;
  background: linear-gradient(135deg, rgba(24, 24, 31, 0.9) 0%, rgba(15, 15, 20, 0.9) 100%);
  border-radius: var(--clipr-radius-xl);
  border-left: 5px solid var(--clipr-purple-light);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);
  position: relative;
}

.header-content {
  position: relative;
  z-index: 1;
}

.public-results-title {
  font-size: clamp(26px, 3vw, 32px);
  font-weight: 600;
  color: var(--clipr-text-primary);
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.public-results-count {
  font-size: 14px;
  color: var(--clipr-text-secondary);
  margin: 6px 0 8px;
  font-weight: 400;
}

.public-results-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
  width: 100%;
}

.brand-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #d946ef, #a855f7);
  color: #fff;
  font-size: 16px;
  box-shadow: 0 8px 22px rgba(168, 85, 247, 0.45);
}

.brand-text {
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--clipr-text-primary);
}

.public-results-subtitle {
  font-size: 14px;
  color: var(--clipr-text-secondary);
  margin: 0;
  font-weight: 400;
}

.public-results-state {
  padding: 60px 20px;
}

.loading-icon {
  font-size: 32px;
  color: var(--clipr-purple-light);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.public-results-clips-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-right: 4px;
}

/* Layout horizontal mejorado estilo clipr */
.public-result-clip-item {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 30px;
  padding: 24px;
  border-radius: var(--clipr-radius-xl);
  background-color: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
  border: 1px solid var(--clipr-border-color);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.7);
  transition: all 0.3s ease;
  cursor: default;
  position: relative;
}

.public-result-clip-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 34px 80px rgba(0, 0, 0, 0.9), 0 0 50px var(--clipr-purple-glow);
  border-color: var(--clipr-border-purple);
  background: radial-gradient(circle at top left, rgba(168, 85, 247, 0.12), transparent 55%),
    var(--clipr-bg-elevated);
}

.public-result-clip-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #a855f7, #6366f1);
  border-radius: 16px 16px 0 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.public-result-clip-item:hover::before {
  opacity: 1;
}

.public-result-clip-media {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.public-result-clip-thumbnail-wrapper {
  width: 100%;
  padding-bottom: 177.78%; /* 16:9 aspect ratio */
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #18181b, #020617);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.7);
}

.video-duration-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  z-index: 5;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  gap: 4px;
}

.video-duration-badge i {
  font-size: 11px;
}

.public-result-clip-item:hover .public-result-clip-thumbnail-wrapper {
  transform: scale(1.03);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.95);
}

.public-result-clip-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border: none;
  background-color: #000;
  border-radius: 10px;
}

.public-result-clip-video--hidden-preview {
  opacity: 0;
}

/* Fix fullscreen video to show full clip without zoom */
.public-result-clip-video:-webkit-full-screen {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.public-result-clip-video:-moz-full-screen {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.public-result-clip-video:-ms-fullscreen {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.public-result-clip-video:fullscreen {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

/* Ocultar barra de progreso y tiempos pero mantener botón de play/pause */
.public-result-clip-thumbnail-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.public-play-icon {
  font-size: 32px;
  color: #fff;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.public-center-play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  border-radius: 999px;
  border: 0;
  background: rgba(15, 23, 42, 0.82);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  cursor: pointer;
  z-index: 6;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
  transition: transform 0.2s ease, background 0.2s ease, opacity 0.2s ease;
}

.public-center-play-btn:hover {
  transform: translate(-50%, -50%) scale(1.06);
  background: rgba(15, 23, 42, 0.94);
}

.public-result-clip-item:hover .public-play-icon {
  opacity: 1;
}

.public-result-clip-overlay {
  position: absolute;
  inset: 0;
  border-radius: 10px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent);
  z-index: 2;
}

.public-result-clip-item:hover .public-result-clip-overlay {
  opacity: 1;
}

.public-result-clip-thumbnail-wrapper::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 12px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), transparent);
  z-index: 1;
}

.public-result-clip-item:hover .public-result-clip-thumbnail-wrapper::after {
  opacity: 1;
}

.public-result-clip-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  padding-right: 100px; /* Espacio para el score */
}

.public-result-clip-actions {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.public-result-clip-header {
  margin-bottom: 16px;
}

.public-result-clip-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--clipr-text-primary);
  margin-bottom: 12px;
  line-height: 1.4;
  transition: color 0.3s ease;
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.clip-number {
  margin-top: 0;
  position: relative;
  top: 3px;
  font-size: 32px;
  font-weight: 800;
  color: #667eea;
  background: linear-gradient(135deg, #a855f7, #6366f1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.public-result-clip-item:hover .clip-number {
  transform: scale(1.1);
}

.public-result-clip-item:hover .public-result-clip-title {
  color: var(--clipr-purple-light);
}

.public-result-clip-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  align-items: center;
}

.status-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  text-transform: uppercase;
  background: rgba(15, 23, 42, 0.9);
  color: var(--clipr-text-secondary);
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--clipr-border-color);
  letter-spacing: 0.3px;
  line-height: 1;
  transition: all 0.3s ease;
}

.status-icon {
  font-size: 12px;
}

.status-pill:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Estados de éxito/completado - Verde */
.status-ready,
.status-rendered,
.status-scheduled_to_post,
.status-posted,
.status-completed,
.status-accepted {
  border-color: rgba(34, 197, 94, 0.6);
  color: #4ade80;
}

/* Estados pendientes - Morado */
.status-pending {
  border-color: rgba(168, 85, 247, 0.6);
  color: var(--clipr-purple-light);
}

/* Estados en proceso - Azul */
.status-processing,
.status-rendering,
.status-in_progress {
  border-color: rgba(34, 211, 238, 0.6);
  color: #93c5fd;
}

/* Estados de error/rechazado - Rojo */
.status-failed,
.status-rejected,
.status-error,
.status-post_failed {
  border-color: rgba(248, 113, 113, 0.6);
  color: #fecaca;
}

.public-timestamp {
  font-size: 13px;
  color: var(--clipr-text-tertiary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.public-timestamp i {
  font-size: 12px;
  opacity: 0.7;
}

.public-result-clip-item:hover .public-result-clip-actions {
  border-top-color: rgba(148, 163, 184, 0.25);
}

.public-download-btn,
.public-delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 10px 18px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.public-download-btn {
  border: 1px solid rgba(168, 85, 247, 0.5);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(99, 102, 241, 0.22));
  color: #e9d5ff;
}

.public-download-btn i {
  font-size: 16px;
  opacity: 0.95;
}

.public-download-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(216, 180, 254, 0.8);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.35), rgba(99, 102, 241, 0.38));
  box-shadow: 0 8px 20px rgba(109, 40, 217, 0.3);
}

.public-download-btn:active:not(:disabled) {
  transform: translateY(0);
}

.public-download-btn:disabled {
  opacity: 0.55;
  cursor: wait;
}

.public-delete-btn {
  border: 1px solid rgba(248, 113, 113, 0.45);
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(127, 29, 29, 0.2));
  color: #fecaca;
}

.public-delete-btn i {
  font-size: 16px;
  opacity: 0.95;
}

.public-delete-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(252, 165, 165, 0.7);
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.28), rgba(127, 29, 29, 0.35));
  box-shadow: 0 8px 20px rgba(185, 28, 28, 0.25);
}

.public-delete-btn:active:not(:disabled) {
  transform: translateY(0);
}

.public-delete-btn:disabled {
  opacity: 0.55;
  cursor: wait;
}

/* Snackbar */
.snackbar-fade-enter-active,
.snackbar-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.snackbar-fade-enter-from,
.snackbar-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

.public-snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translate(-50%, 0);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  z-index: 3100;
}

.public-snackbar i {
  font-size: 18px;
  opacity: 0.95;
}

.public-snackbar--download {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.95), rgba(99, 102, 241, 0.95));
  border: 1px solid rgba(216, 180, 254, 0.5);
}

.public-snackbar--delete {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.95), rgba(153, 27, 27, 0.95));
  border: 1px solid rgba(252, 165, 165, 0.4);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.confirm-modal {
  width: min(560px, 100%);
  background: linear-gradient(135deg, rgba(24, 24, 31, 0.98), rgba(15, 23, 42, 0.98));
  border: 1px solid var(--clipr-border-color);
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.65), 0 0 30px rgba(168, 85, 247, 0.25);
  color: var(--clipr-text-primary);
}

.confirm-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.confirm-modal-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.35), rgba(99, 102, 241, 0.35));
  color: #f5f3ff;
  flex-shrink: 0;
}

.confirm-modal-header-icon--danger {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.35), rgba(127, 29, 29, 0.4));
  color: #fecaca;
}

.confirm-modal-title {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
}

.confirm-modal-subtitle {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 13px;
}

.confirm-modal-close {
  margin-left: auto;
  border: 1px solid var(--clipr-border-color);
  background: transparent;
  color: var(--clipr-text-secondary);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.confirm-modal-close:hover:not(:disabled) {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
  background: transparent;
}

.confirm-modal-body {
  padding: 18px 22px;
}

.delete-confirm-summary {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}

.delete-confirm-label {
  display: block;
  color: var(--clipr-text-tertiary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.delete-confirm-title {
  font-size: 16px;
  line-height: 1.4;
}

.delete-confirm-warning {
  margin: 0;
  font-size: 14px;
  color: #fecaca;
}

.delete-confirm-error {
  margin: 12px 0 0;
  color: #fca5a5;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.confirm-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 22px 20px;
}

.confirm-modal-btn {
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  padding: 9px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-modal-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.confirm-modal-btn--secondary {
  background: rgba(15, 23, 42, 0.7);
  border-color: rgba(148, 163, 184, 0.3);
  color: var(--clipr-text-secondary);
}

.confirm-modal-btn--primary {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.92), rgba(99, 102, 241, 0.9));
  color: #fff;
  box-shadow: 0 8px 22px rgba(109, 40, 217, 0.28);
}

.confirm-modal-btn--danger {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.92), rgba(153, 27, 27, 0.95));
  color: #fff;
  box-shadow: 0 8px 22px rgba(185, 28, 28, 0.3);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 700;
  font-size: 14px;
  color: var(--clipr-purple-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.transcript-icon {
  font-size: 18px;
  color: #764ba2;
}

.section-content {
  font-size: 16px;
  color: var(--clipr-text-primary);
  line-height: 1.8;
  font-weight: 500;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--clipr-border-color), transparent);
}

.divider-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--clipr-purple-light);
  box-shadow: 0 0 8px var(--clipr-purple-glow);
}

/* Transcript mejorado */
.public-result-clip-transcript {
  margin-top: 20px;
  margin-bottom: 0;
  padding: 24px;
  background: linear-gradient(135deg, rgba(24, 24, 31, 0.95), rgba(15, 23, 42, 0.98));
  border-radius: 12px;
  border: 1px solid var(--clipr-border-color);
  transition: all 0.3s ease;
}

.public-result-clip-item:hover .public-result-clip-transcript {
  box-shadow: 0 4px 12px rgba(118, 75, 162, 0.1);
  border-color: #cbd5e0;
}

.public-result-clip-transcript .section-content {
  font-size: 17px;
  color: var(--clipr-text-secondary);
  font-style: italic;
  line-height: 1.8;
}

/* Score prominente en esquina superior derecha */
.public-result-clip-score {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: transform 0.3s ease;
}

.public-result-clip-item:hover .public-result-clip-score {
  transform: translateY(-2px);
}

.score-value {
  font-size: 48px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
  transition: all 0.3s ease;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.score-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

/* Score colors dinámicos */
.score-excellent .score-value {
  color: #4ade80;
  text-shadow: 0 2px 10px rgba(22, 163, 74, 0.5);
}

.score-excellent .score-label {
  color: #16a34a;
}

.score-good .score-value {
  color: #fdba74;
  text-shadow: 0 2px 10px rgba(249, 115, 22, 0.5);
}

.score-good .score-label {
  color: #d97706;
}

.score-low .score-value {
  color: #fca5a5;
  text-shadow: 0 2px 10px rgba(248, 113, 113, 0.5);
}

.score-low .score-label {
  color: #fecaca;
}

.public-result-clip-item:hover .score-excellent .score-value {
  color: #16a34a;
  transform: scale(1.08);
}

.public-result-clip-item:hover .score-good .score-value {
  color: #d97706;
  transform: scale(1.08);
}

.public-result-clip-item:hover .score-low .score-value {
  color: #dc2626;
  transform: scale(1.08);
}


.empty-state-icon {
  font-size: 48px;
  color: #cbd5e0;
  opacity: 0.6;
}

.public-results-header {
  animation: fadeInDown 0.5s ease;
}

.public-results-clips-list {
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.public-result-clip-item {
  animation: fadeIn 0.3s ease;
  animation-fill-mode: both;
}

.public-result-clip-item:nth-child(1) {
  animation-delay: 0.1s;
}

.public-result-clip-item:nth-child(2) {
  animation-delay: 0.2s;
}

.public-result-clip-item:nth-child(3) {
  animation-delay: 0.3s;
}

.public-result-clip-item:nth-child(n+4) {
  animation-delay: 0.4s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .public-results-container {
  padding: 30px 24px;
  }

  .public-results-header {
    padding: 20px 16px;
  }

  .public-results-title {
  font-size: 24px;
  }

  .brand-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    font-size: 14px;
  }

  .brand-text {
    font-size: 16px;
    letter-spacing: 0.12em;
  }

  .public-result-clip-item {
    grid-template-columns: 200px minmax(0, 1fr);
    gap: 16px;
    padding: 20px;
  }

  .public-result-clip-content {
    padding-right: 80px; /* Menos espacio para el score en tablets */
  }

  .score-value {
    font-size: 36px;
  }

  .score-label {
    font-size: 11px;
  }

  .public-result-clip-title {
    font-size: 24px;
  }

  .clip-number {
    font-size: 28px;
  }

  .public-result-clip-transcript {
    padding: 20px;
  }

  .public-result-clip-transcript .section-content {
    font-size: 16px;
  }

  .video-duration-badge {
    font-size: 11px;
    padding: 5px 8px;
  }

  .public-result-clip-actions {
    padding-top: 18px;
  }

  .confirm-modal-overlay {
    padding: 16px;
  }

  .confirm-modal-title {
    font-size: 20px;
  }
}

@media (max-width: 575.98px) {
  .public-results-page {
    padding: 20px 12px;
  }

  .public-results-container {
    padding: 24px 16px;
    border-radius: var(--clipr-radius-xl);
  }

  .public-results-header {
    margin-bottom: 30px;
    padding: 18px 14px;
    border-radius: 10px;
    border-left-width: 4px;
  }

  .public-results-title {
    font-size: 22px;
  }

  .public-results-brand {
    margin-bottom: 8px;
  }

  .brand-icon {
    width: 28px;
    height: 28px;
    border-radius: 9px;
    font-size: 12px;
  }

  .brand-text {
    font-size: 14px;
    letter-spacing: 0.1em;
  }

  .public-results-subtitle {
    font-size: 14px;
  }

  .public-results-clips-list {
    gap: 20px;
  }

  .public-result-clip-item {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    padding: 20px;
  }

  .public-result-clip-media {
    max-width: 100%;
    width: 100%;
    margin: 0 auto 16px;
  }

  .public-result-clip-thumbnail-wrapper {
    max-width: 100%;
  }

  .public-result-clip-content {
    padding-right: 70px; /* Espacio para el score en móvil */
  }

  .score-value {
    font-size: 32px;
  }

  .score-label {
    font-size: 10px;
  }

  .public-result-clip-title {
    font-size: 22px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .clip-number {
    font-size: 24px;
  }

  .public-result-clip-transcript {
    padding: 18px;
    margin-top: 16px;
  }

  .public-result-clip-transcript .section-content {
    font-size: 15px;
  }

  .section-header {
    font-size: 12px;
  }

  .transcript-icon {
    font-size: 16px;
  }

  .video-duration-badge {
    font-size: 10px;
    padding: 4px 8px;
    bottom: 6px;
    right: 6px;
  }

  .status-pill {
    padding: 2px 7px;
    font-size: 9px;
  }

  .public-result-clip-actions {
    padding-top: 16px;
    gap: 8px;
  }

  .public-download-btn,
  .public-delete-btn {
    font-size: 12px;
    padding: 8px 14px;
    gap: 6px;
  }

  .public-download-btn i,
  .public-delete-btn i {
    font-size: 14px;
  }

  .confirm-modal-header {
    padding: 16px;
  }

  .confirm-modal-body {
    padding: 14px 16px;
  }

  .confirm-modal-footer {
    padding: 0 16px 16px;
  }

  .public-snackbar {
    bottom: 16px;
    padding: 12px 18px;
    font-size: 13px;
  }

  .public-snackbar i {
    font-size: 16px;
  }

  .status-icon {
    font-size: 11px;
  }
}
</style>

